import { Trans, useTranslation } from "react-i18next"
import {
  Text,
  Group,
  Badge,
  Button,
  Paper,
  Grid,
  Stack,
  Box,
} from "@mantine/core"
import type { Document } from "../models/document"
import { getMimeColor } from "../constants"

function EmptyValue() {
  return (
    <Text size="sm" fs="italic" c="gray.5">
      Não preenchido
    </Text>
  )
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group gap="xs" wrap="nowrap" align="flex-start">
      <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" style={{ minWidth: 110, flexShrink: 0 }}>
        {label}
      </Text>
      <Box style={{ flex: 1 }}>{children}</Box>
    </Group>
  )
}

interface Props {
  doc: Document
}

export default function DocumentDetailContent({ doc }: Props) {
  const { t } = useTranslation()
  const mimeColor = getMimeColor(doc.mime_type)
  const validityLabel = doc.valid_until
    ? doc.is_valid
      ? t("documents.valid")
      : t("documents.expired")
    : "Sem expiração"
  const validityColor = doc.valid_until ? (doc.is_valid ? "green" : "red") : "gray"

return (
    <>
      <Paper
        withBorder
        p="lg"
        mb="md"
        style={{ borderLeft: `4px solid var(--mantine-color-${mimeColor}-5)` }}
      >
        <Group wrap="nowrap" align="flex-start" gap="lg">
          <Box
            style={{
              width: 72,
              height: 72,
              borderRadius: "var(--mantine-radius-md)",
              background: `var(--mantine-color-${mimeColor}-1)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Text size="32px" c={`${mimeColor}.7`}>
              <i className={doc.mime_type_class} />
            </Text>
          </Box>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" mb={6} wrap="wrap">
              <Text fw={700} size="xl" truncate="end" style={{ maxWidth: "100%" }}>
                {doc.real_name}
              </Text>
              <Badge variant="light" color={mimeColor} size="lg">{doc.mime_type_title}</Badge>
              {doc.public ? (
                <Badge variant="light" color="yellow" size="sm">{t("documents.public")}</Badge>
              ) : (
                <Badge variant="light" color="blue" size="sm">{t("documents.private")}</Badge>
              )}
            </Group>
            <Text size="sm" c="dimmed" lineClamp={2}>
              {doc.description || <EmptyValue />}
            </Text>
          </div>
        </Group>
      </Paper>

      <Grid>
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Paper withBorder p="md">
            <Text fw={600} size="sm" mb="md">
              <Trans i18nKey="documents.documentInfo" />
            </Text>
            <Stack gap="sm">
              <MetaRow label={t("documents.description")}>
                <Text>{doc.description || <EmptyValue />}</Text>
              </MetaRow>
              <MetaRow label={t("documents.category")}>
                <Text>{doc.category_name || <EmptyValue />}</Text>
              </MetaRow>
              <MetaRow label={t("documents.mimeType")}>
                <Text>{doc.mime_type_title}</Text>
              </MetaRow>
              <MetaRow label={t("documents.validUntil")}>
                <Text>
                  {doc.valid_until ? new Date(doc.valid_until).toLocaleDateString("pt-PT") : <EmptyValue />}
                </Text>
              </MetaRow>
              <MetaRow label={t("documents.createdAt")}>
                <Text>{doc.created_at ? new Date(doc.created_at).toLocaleDateString("pt-PT") : <EmptyValue />}</Text>
              </MetaRow>
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Stack gap="md">
            <Paper withBorder p="md">
              <Text fw={600} size="sm" mb="md">
                <Trans i18nKey="documents.ownerInfo" />
              </Text>
              <Stack gap="sm">
                {doc.owner_name && (
                  <MetaRow label={t("documents.owner")}>
                    <Text>{doc.owner_name}</Text>
                  </MetaRow>
                )}
                <MetaRow label={t("documents.status")}>
                  <Badge variant="light" color={validityColor} size="sm">
                    {validityLabel}
                  </Badge>
                </MetaRow>
              </Stack>
            </Paper>

            {doc.rules && doc.rules.length > 0 && (
              <Paper withBorder p="md">
                <Text fw={600} size="sm" mb="sm">
                  <Trans i18nKey="documents.visibility" />
                </Text>
                <Group gap="xs">
                  {doc.rules.map((role) => (
                    <Badge key={role} variant="light" color="violet" size="sm">{role}</Badge>
                  ))}
                </Group>
              </Paper>
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      <Button
        component="a"
        href={doc.url}
        target="_blank"
        rel="noopener noreferrer"
        leftSection={<i className="fa fa-download" />}
      >
        <Trans i18nKey="common.download" />
      </Button>
    </>
  )
}
